import React, { useState, useEffect } from "react";

const AnimatedCounter = ({ value, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    const startValue = 0;
    const endValue = parseFloat(value);

    const animation = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = (currentTime - startTime) / duration;

      if (progress < 1) {
        const currentCount = startValue + (endValue - startValue) * progress;
        setCount(currentCount);
        requestAnimationFrame(animation);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animation);
  }, [value, duration]);

  return <span>{count.toFixed(2)}%</span>;
};

const LoadingSkeleton = () => (
  <div className="mt-12 px-4 w-full mx-auto">
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {[1, 2, 3].map((index) => (
        <div 
          key={index}
          className="relative bg-white rounded-lg shadow-lg border border-gray-200 p-6 overflow-hidden"
        >
          {/* Professional gradient loading effect */}
          <div className="absolute inset-0">
            <div className="animate-shine absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent" />
          </div>
          
          <div className="relative space-y-6">
            {/* Icon placeholder with subtle pulse */}
            <div className="flex justify-center">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gray-200 to-gray-300 animate-pulse" />
            </div>
            
            {/* Title placeholder with gradient */}
            <div className="space-y-2">
              <div className="h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-md mx-auto w-3/4" />
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded-md mx-auto w-2/3" />
            </div>
            
            {/* Value placeholder with subtle animation */}
            <div className="flex justify-center items-center mt-4">
              <div className="h-10 w-24 bg-gradient-to-r from-gray-200 to-gray-300 rounded-md animate-pulse" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function EndpointBoxes({ data }) {
  if (!data) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="mt-12 px-4 w-full mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Overall Availability Card */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <div className="text-[#003366]">
            <div className="flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-center mb-2">
              Overall Availability
            </h3>
            <p className="text-center text-sm text-gray-600 mb-4">
              Average availability of all endpoints
            </p>
            <div className="text-4xl font-bold text-center text-green-600">
              <AnimatedCounter value={data.overallAvailability} />
            </div>
          </div>
        </div>

        {/* Running Endpoints Card */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <div className="text-[#003366]">
            <div className="flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-center mb-2">
              Running Endpoints
            </h3>
            <p className="text-center text-sm text-gray-600 mb-4">
              Currently active endpoints
            </p>
            <div className="text-4xl font-bold text-center text-green-600">
              {data.runningEndpoints}
            </div>
          </div>
        </div>

        {/* Failed Endpoints Card */}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
          <div className="text-[#003366]">
            <div className="flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-center mb-2">
              Failed Endpoints
            </h3>
            <p className="text-center text-sm text-gray-600 mb-4">
              Currently failed endpoints
            </p>
            <div className="text-4xl font-bold text-center text-red-600">
              {data.failedEndpoints}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}